import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { ColumnDefinition, DataTableItem } from './types'

interface DataTableSkeletonProps<T extends DataTableItem> {
  columns: ColumnDefinition<T>[]
  visibleColumns: string[]
  rowCount?: number
  isScrolledX?: boolean
  hasActions?: boolean
  noAnimation?: boolean
  hasSpacerColumn?: boolean
}

export function DataTableSkeleton<T extends DataTableItem>({
  columns,
  visibleColumns,
  rowCount = 20,
  isScrolledX = false,
  hasActions = false,
  noAnimation = false,
  hasSpacerColumn = false
}: DataTableSkeletonProps<T>) {
  const animationClass = noAnimation ? 'animate-none' : ''

  return (
    <>
      {Array.from({ length: rowCount }).map((_, index) => (
        <tr key={`skeleton-${index}`} className="border-b border-table-grid">
          {columns
            .filter(col => visibleColumns.includes(col.id))
            .map(column => {
              const isSticky = column.sticky === true
              const isSelectColumn = column.id === 'select' || column.id === 'name'
              const skeletonWidth = column.skeletonWidth || 'w-24'

              return (
                <td
                  key={column.id}
                  className={`px-4 py-2 relative overflow-hidden bg-table ${isSelectColumn ? 'pl-checkbox-cell' : ''} ${isSticky ? 'sticky left-0 z-10' : ''}`}
                >
                  {isSelectColumn ? (
                    <div className="flex items-center gap-3">
                      <Skeleton className={cn("h-5 w-5 rounded-full ml-2.5", animationClass)} />
                      <Skeleton className={cn("h-8 w-8 rounded-full", animationClass)} />
                    </div>
                  ) : (
                    <Skeleton className={cn(`h-4 ${skeletonWidth}`, animationClass)} />
                  )}
                  <div className="absolute top-0 right-0 h-full bg-table-grid" style={{ width: '1px' }} />
                  {isSticky && (
                    <div
                      className={`absolute top-0 -right-4 h-full w-4 pointer-events-none transition-opacity duration-200 ${isScrolledX ? 'opacity-100' : 'opacity-0'}`}
                      style={{ background: 'linear-gradient(to right, var(--scroll-shadow), transparent)' }}
                    />
                  )}
                </td>
              )
            })}
          {hasActions && (
            <td className="px-4 py-2 bg-table relative">
              <Skeleton className={cn("h-8 w-8 rounded-full", animationClass)} />
              <div className="absolute top-0 right-0 h-full bg-table-grid" style={{ width: '1px' }} />
            </td>
          )}
          {hasSpacerColumn && <td className="bg-table" />}
        </tr>
      ))}
    </>
  )
}
